import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { useHubRideActionConfirm, useRebuildRoute } from "@ryde/redux";
import {
  CourseRouteTypeEnum,
  DirectionsServiceEnum,
  editFormDataInitialSnapshotAF,
  errors,
  formatRebuildRouteTooltip,
  formatRouteRebuildSuccessToast,
  HubRideActionKind,
  IRebuildRouteDefaultActionOptions,
  IRideDto,
  isCustomRebuildRouteType,
  lastRouteRebuildAtAtom,
  lastSelectedRebuildTypeAtom,
  Log,
  PROVIDERS_WITH_AVOID_TOLLS,
  rebuildRouteLoadingAtom,
  RebuildRouteTypeEnum,
  resolveInitialRebuildRouteProvider,
  resolveRebuildRouteProvider,
  RideCrudModeKeyTuple,
  rideHasUnsavedStationChangeAtom,
  toDisplayedRebuildRouteType,
  useSystemParams,
  warnAndToastException,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export const useRebuildRouteDropdown = (rideDto: Partial<IRideDto>) => {
  const { t } = useTranslation();
  const { rebuildRideRoute } = useRebuildRoute();
  const { confirmHubRideAction } = useHubRideActionConfirm();
  const systemParams = useSystemParams();
  const rideInitialSnapshot = useRecoilValue(
    editFormDataInitialSnapshotAF(RideCrudModeKeyTuple),
  );

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useRecoilState(rebuildRouteLoadingAtom);
  const lastSelectedRebuildType = useRecoilValue(lastSelectedRebuildTypeAtom);
  const lastRouteRebuildAt = useRecoilValue(lastRouteRebuildAtAtom);
  const setLastSelectedRebuildType = useSetRecoilState(
    lastSelectedRebuildTypeAtom,
  );
  const setRideHasUnsavedStationChange = useSetRecoilState(
    rideHasUnsavedStationChangeAtom,
  );

  const systemDefaultProvider =
    systemParams.directionsService || DirectionsServiceEnum.Google;
  const savedLastUsedProvider =
    rideInitialSnapshot?.metadata?.directionsService;
  const currentRebuildProvider = rideDto?.metadata?.directionsService;
  const defaultRebuildProvider = useMemo(
    () =>
      resolveInitialRebuildRouteProvider({
        lastUsedProvider: savedLastUsedProvider,
        systemDefaultProvider,
      }),
    [savedLastUsedProvider, systemDefaultProvider],
  );
  const rebuildRouteTooltip = useMemo(
    () =>
      formatRebuildRouteTooltip({
        baseLabel: t("general.REBUILD_SAVE_CACHE"),
        lastUsedProvider: savedLastUsedProvider,
        systemDefaultProvider,
      }),
    [savedLastUsedProvider, systemDefaultProvider, t],
  );

  const [selectedProvider, setSelectedProvider] =
    useState<DirectionsServiceEnum | null>(() => defaultRebuildProvider);
  const [avoidTolls, setAvoidTolls] = useState<boolean>(
    systemParams.avoidTolls ?? false,
  );
  // On by default: a custom rebuild has always recalculated live instead of reusing the saved ML path.
  const [skipMlPath, setSkipMlPath] = useState(true);
  const isShuttleLikeRide =
    rideDto?.courseRouteType === CourseRouteTypeEnum.Shuttle ||
    rideDto?.courseRouteType === CourseRouteTypeEnum.Flex;
  const courseId = (rideDto as IRideDto)?.courseId;
  const isCourseRide = Boolean(courseId);
  const getDefaultRouteOption = (): RebuildRouteTypeEnum => {
    if (isShuttleLikeRide) {
      return RebuildRouteTypeEnum.KeepOrder;
    }
    if (isCourseRide) {
      return RebuildRouteTypeEnum.KeepOrder;
    }
    return RebuildRouteTypeEnum.BestFit;
  };
  const [routeOptionTouched, setRouteOptionTouched] = useState(false);
  const [providerTouched, setProviderTouched] = useState(false);
  const [routeOption, setRouteOptionState] = useState<RebuildRouteTypeEnum>(
    () => getDefaultRouteOption(),
  );
  const setRouteOption = (option: RebuildRouteTypeEnum): void => {
    setRouteOptionTouched(true);
    setRouteOptionState(option);
  };
  const setSelectedProviderWithTouch = (
    provider: DirectionsServiceEnum,
  ): void => {
    setProviderTouched(true);
    setSelectedProvider(provider);
  };

  useEffect(() => {
    setProviderTouched(false);
  }, [rideDto.id]);

  useEffect(() => {
    if (providerTouched) {
      return;
    }
    setSelectedProvider(defaultRebuildProvider);
  }, [defaultRebuildProvider, providerTouched, rideDto.id]);

  useEffect(() => {
    if (routeOptionTouched) {
      return;
    }
    if (isShuttleLikeRide || isCourseRide) {
      setRouteOptionState(RebuildRouteTypeEnum.KeepOrder);
      return;
    }
    setRouteOptionState(RebuildRouteTypeEnum.BestFit);
  }, [routeOptionTouched, isShuttleLikeRide, isCourseRide]);

  useEffect(() => {
    // For shuttle-like rides, station order must remain as-is.
    // If user previously selected an option that reorders stations, force KeepOrder.
    if (
      isShuttleLikeRide &&
      isCourseRide &&
      (routeOption === RebuildRouteTypeEnum.ProviderOptimization ||
        routeOption === RebuildRouteTypeEnum.BestFit ||
        routeOption === RebuildRouteTypeEnum.OptimizeStationOrderOsrmFirst)
    ) {
      setRouteOptionState(RebuildRouteTypeEnum.KeepOrder);
    }
  }, [isCourseRide, isShuttleLikeRide, routeOption]);

  const handleDropdownToggle = useEvent((event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(prevOpen => !prevOpen);
  });

  const handleClose = useEvent((_event: Event | React.SyntheticEvent) => {
    setOpen(false);
  });

  const supportsAvoidTolls = selectedProvider
    ? PROVIDERS_WITH_AVOID_TOLLS.includes(selectedProvider)
    : true;

  const handleDefaultRebuild = useEvent(
    async (
      rebuildType: RebuildRouteTypeEnum,
      options?: IRebuildRouteDefaultActionOptions,
    ) => {
      if (!rideDto?.rideStart) {
        Log.warnAndToast(t(errors.ride.noInitialDateInRide));
        return;
      }

      if (isLoading) {
        return;
      }

      const isHubActionConfirmed = await confirmHubRideAction({
        rides: [rideDto],
        actionKind: HubRideActionKind.Modify,
      });

      if (!isHubActionConfirmed) {
        return;
      }

      setIsLoading(true);
      setOpen(false);

      try {
        const recountResult = await rebuildRideRoute(rideDto, {
          rebuildType,
          provider: options?.provider ?? defaultRebuildProvider,
        });
        if (recountResult.success) {
          if (rideDto.id) {
            setRideHasUnsavedStationChange(false);
          }

          setLastSelectedRebuildType(toDisplayedRebuildRouteType(rebuildType));

          Log.infoAndToast(
            formatRouteRebuildSuccessToast({
              baseMessage: t("ride.RIDE_ROUTE_REBUILD_SUCCESSFULLY"),
              provider:
                recountResult.data?.directionsService ?? defaultRebuildProvider,
              routeRebuildSource: recountResult.data?.routeRebuildSource,
              t,
            }),
          );
        } else {
          warnAndToastSimpleResult(recountResult, t);
        }
      } catch (error) {
        warnAndToastException({
          error,
          translationFunction: t,
          fallbackTranslationKey: errors.ride.rebuildRouteFailed,
        });
      } finally {
        setIsLoading(false);
      }
    },
  );

  const handleTopDefaultRebuild = useEvent(
    () => void handleDefaultRebuild(RebuildRouteTypeEnum.Default),
  );

  const handleRebuild = useEvent(async () => {
    if (!rideDto?.rideStart) {
      Log.warnAndToast(t(errors.ride.noInitialDateInRide));
      return;
    }

    if (isLoading) {
      return;
    }

    const isHubActionConfirmed = await confirmHubRideAction({
      rides: [rideDto],
      actionKind: HubRideActionKind.Modify,
    });

    if (!isHubActionConfirmed) {
      return;
    }

    const rebuildType =
      routeOption || RebuildRouteTypeEnum.ProviderOptimization;

    setIsLoading(true);

    try {
      const provider = resolveRebuildRouteProvider({
        selectedProvider,
        lastUsedProvider: savedLastUsedProvider,
        systemDefaultProvider,
      });

      const recountResult = await rebuildRideRoute(rideDto, {
        rebuildType,
        avoidTolls,
        provider,
        skipMlPath,
      });

      if (recountResult.success) {
        if (rideDto.id) {
          setRideHasUnsavedStationChange(false);
        }

        setLastSelectedRebuildType(toDisplayedRebuildRouteType(rebuildType));

        Log.infoAndToast(
          formatRouteRebuildSuccessToast({
            baseMessage: t("ride.RIDE_ROUTE_REBUILD_SUCCESSFULLY"),
            provider: recountResult.data?.directionsService ?? provider,
            routeRebuildSource: recountResult.data?.routeRebuildSource,
            t,
          }),
        );
      } else {
        warnAndToastSimpleResult(recountResult, t);
      }
    } catch (error) {
      warnAndToastException({
        error,
        translationFunction: t,
        fallbackTranslationKey: errors.ride.rebuildRouteFailed,
      });
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  });

  const hasCustomRebuildType =
    isCustomRebuildRouteType(rideDto.metadata?.rebuildRouteType) ||
    isCustomRebuildRouteType(lastSelectedRebuildType);

  return {
    open,
    isLoading,
    systemDefaultProvider,
    savedLastUsedProvider,
    currentRebuildProvider,
    defaultRebuildProvider,
    rebuildRouteTooltip,
    selectedProvider,
    setSelectedProvider: setSelectedProviderWithTouch,
    avoidTolls,
    setAvoidTolls,
    skipMlPath,
    setSkipMlPath,
    routeOption,
    setRouteOption,
    supportsAvoidTolls,
    systemDefaultAvoidTolls: systemParams.avoidTolls ?? false,
    hasCustomRebuildType,
    lastSelectedRebuildType,
    lastRouteRebuildAt,
    handleDropdownToggle,
    handleClose,
    handleTopDefaultRebuild,
    handleDefaultRebuild,
    handleRebuild,
  };
};
